import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { aiProxy, isProxyConfigured } from '../services/ai/proxyClient';

const ORG_CONFIG_PATH = 'artifacts/bz-tracker/appSettings/aiConfig';

const DEFAULT_ORG = {
  defaultProvider: 'gemini',
  defaultModel: 'gemini-3-flash-preview',
  fallback: [],
  allowUserModelSelection: true,
};

/**
 * User-facing AI configuration:
 *  - reads org policy + the user's status mirror (no secrets)
 *  - lets the user pick provider/model (written to the mirror)
 *  - saves/clears the user's own API key via the proxy (encrypted server-side)
 */
export const useAIConfig = (userId) => {
  const [orgConfig, setOrgConfig] = useState(DEFAULT_ORG);
  const [mirror, setMirror] = useState({});
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Org policy.
  useEffect(() => {
    if (!db) return undefined;
    const ref = doc(db, ORG_CONFIG_PATH);
    return onSnapshot(
      ref,
      (snap) => setOrgConfig(snap.exists() ? { ...DEFAULT_ORG, ...snap.data() } : DEFAULT_ORG),
      (err) => setError(err.message)
    );
  }, []);

  // User status mirror (the `ai` map on the user doc).
  useEffect(() => {
    if (!db || !userId) {
      setIsLoading(false);
      return undefined;
    }
    const ref = doc(db, `artifacts/bz-tracker/users/${userId}`);
    return onSnapshot(
      ref,
      (snap) => {
        setMirror((snap.exists() && snap.data().ai) || {});
        setIsLoading(false);
      },
      (err) => {
        setError(err.message);
        setIsLoading(false);
      }
    );
  }, [userId]);

  // Available providers/models from the proxy.
  useEffect(() => {
    if (!isProxyConfigured()) return;
    aiProxy
      .listProviders()
      .then((data) => setProviders(data.providers || []))
      .catch(() => setProviders([]));
  }, []);

  const provider = mirror.provider || orgConfig.defaultProvider;
  const model = mirror.model || orgConfig.defaultModel;
  const lockedByAdmin = mirror.lockedByAdmin === true;
  const usingHouseKey = mirror.byokDisabled === true && mirror.useHouseKey === true;
  // A usable key exists when the stored key matches the active provider.
  const hasUsableKey = mirror.hasKey === true && mirror.keyProvider === provider;

  const setSelection = useCallback(
    async (nextProvider, nextModel) => {
      if (!db || !userId) return;
      if (lockedByAdmin) throw new Error('Your AI configuration is managed by an admin.');
      const ref = doc(db, `artifacts/bz-tracker/users/${userId}`);
      await updateDoc(ref, { 'ai.provider': nextProvider, 'ai.model': nextModel });
    },
    [userId, lockedByAdmin]
  );

  const saveKey = useCallback(
    async (keyProvider, apiKey, keyModel) => {
      if (lockedByAdmin) throw new Error('Your AI configuration is managed by an admin.');
      return aiProxy.saveKey(keyProvider, apiKey, keyModel);
    },
    [lockedByAdmin]
  );

  const clearKey = useCallback(
    async (keyProvider) => {
      if (lockedByAdmin) throw new Error('Your AI configuration is managed by an admin.');
      return aiProxy.clearKey(keyProvider);
    },
    [lockedByAdmin]
  );

  return {
    isLoading,
    error,
    proxyConfigured: isProxyConfigured(),
    orgConfig,
    provider,
    model,
    providers,
    lockedByAdmin,
    usingHouseKey,
    allowModelSelection: orgConfig.allowUserModelSelection !== false,
    hasKey: mirror.hasKey === true,
    hasUsableKey,
    keyProvider: mirror.keyProvider || null,
    last4: mirror.last4 || null,
    needsKey: mirror.needsKey === true,
    setSelection,
    saveKey,
    clearKey,
  };
};
